import { useUpdateIndividualReportLastOutput } from '@/features/project/groups/detail/_hooks/group';
import { useChangeBrowserTabTitleEffect } from '@/hooks';
import { isPS } from '@/utils';
import { type RefObject, useCallback, useMemo } from 'react';
import { useReactToPrint } from 'react-to-print';

/**
 * プロジェクト帳票の情報型定義
 */
type ProjectLedgerInfo = {
  fiscalYear: string | number;
  name: string;
  projectNumber: string | null;
};

/**
 * ファイル名として安全でない文字を置換する
 * @param filename - サニタイズ対象の文字列
 * @returns サニタイズされた文字列
 */
const sanitizeFileName = (filename: string): string => {
  // Windows/macOS/Linuxで使用できない文字を置換
  // / \ : * ? " < > |
  return filename
    .replace(/[/\\:*?"<>|]/g, '_')
    .replace(/\s+/g, '-') // 連続する空白をハイフンに
    .replace(/_{2,}/g, '_') // 連続するアンダースコアを1つに
    .replace(/^[.-]/, '') // 先頭のドットやハイフンを除去
    .slice(0, 200); // ファイル名長制限
};

/**
 * プロジェクト帳票のファイル名を生成する
 * @param fiscalYear - 年度
 * @param ministryName - 省庁名
 * @param projectNumber - 予算事業ID
 * @param name - 事業名
 * @returns サニタイズされたファイル名
 */
const buildProjectLedgerFileName = (
  fiscalYear: string | number,
  ministryName: string,
  projectNumber: string | null,
  name: string,
): string => {
  const sanitizedNumber = sanitizeFileName(projectNumber ?? '');
  const sanitizedName = sanitizeFileName(name);
  return `${fiscalYear}-${ministryName}-${sanitizedNumber}-${sanitizedName}`;
};

/**
 * プロジェクト帳票ページのタイトルを生成するフック
 */
const useProjectLedgerPageTitle = (
  project?: ProjectLedgerInfo,
  ministryName?: string,
): string | undefined => {
  return useMemo(
    () =>
      project &&
      ministryName &&
      buildProjectLedgerFileName(
        project.fiscalYear,
        ministryName,
        project.projectNumber,
        project.name,
      ),
    [ministryName, project],
  );
};

/**
 * プロジェクト帳票のPDF出力フックを提供
 */
export const useProjectLedgerPrint = ({
  contentRef,
  documentTitle,
  onAfterPrint,
}: {
  contentRef: RefObject<HTMLDivElement | null>;
  documentTitle?: string;
  onAfterPrint?: () => void;
}) => {
  return useReactToPrint({
    contentRef,
    pageStyle: '@page { size: A4 landscape; margin: 10mm 5mm; }',
    documentTitle,
    onAfterPrint,
  });
};

/**
 * プロジェクト帳票のタイトルを生成する関数
 */
export const createProjectLedgerTitle = (
  project?: ProjectLedgerInfo,
  ministryName?: string,
): string => {
  if (!project || !ministryName) {
    return 'project-ledger';
  }
  return buildProjectLedgerFileName(
    project.fiscalYear,
    ministryName,
    project.projectNumber,
    project.name,
  );
};

/**
 * プロジェクト帳票ページの印刷機能を統合したフック
 *
 * - ドキュメントタイトルの生成
 * - ブラウザタブタイトルの設定
 * - 印刷処理
 * - 内部サイトの場合は最終出力日時の更新
 * を一元管理します。
 */
export const useProjectLedgerPagePrint = ({
  contentRef,
  project,
  ministryName,
  projectId,
}: {
  contentRef: RefObject<HTMLDivElement | null>;
  project?: ProjectLedgerInfo;
  ministryName?: string;
  projectId?: string;
}) => {
  // タイトルを生成
  const title = useProjectLedgerPageTitle(project, ministryName);

  // ブラウザタブのタイトルを設定
  useChangeBrowserTabTitleEffect({
    title,
    hideFallback: true,
  });

  // 最終出力日時更新のmutation
  const { mutate: updateLastOutput } = useUpdateIndividualReportLastOutput();

  // 印刷処理のフック
  const handlePrint = useProjectLedgerPrint({
    contentRef,
    documentTitle: title,
    onAfterPrint: () => {
      // 印刷完了後に最終出力日時を更新
      if (projectId && !isPS()) {
        updateLastOutput({ id: projectId });
      }
    },
  });

  // 印刷実行関数
  const handlePrintPdf = useCallback(() => {
    handlePrint();
  }, [handlePrint]);

  return {
    title,
    handlePrintPdf,
  };
};
